import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { FieldValues, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import { SelectField, NumberField } from 'common/forms/Field';
import { TControllerProps } from 'common/types';
import { IScoringFragment, ScoringSystemsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { EScoringSystemOption } from 'certificate/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const ScoringField = <TFieldValues extends FieldValues>({
    name,
    control: formControl,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();

    const {
        data: scoringSystemsData,
        loading: scoringSystemsLoading,
        error: scoringSystemsError,
    } = useQuery(ScoringSystemsDocument);

    useEffect(() => {
        if (scoringSystemsError) {
            showApolloError(scoringSystemsError);
        }
    }, [scoringSystemsError, showApolloError]);

    const control = asTypedControl<IScoringFragment>(name, formControl);

    const score = useWatch({
        control,
        name: `${name}.score`,
    });

    const scoringSystems = scoringSystemsData?.scoringSystems || [];

    const scoringSystemsOptions = useMemo(
        () => [
            {
                label: translate('certificateForm.noScoring'),
                value: EScoringSystemOption.NoScoringSystem,
            },
            ...scoringSystems.map((scoringSystem) => ({
                label:
                    Number(score) === 1
                        ? scoringSystem.nameSingular
                        : scoringSystem.namePlural,
                value: scoringSystem.id,
            })),
        ],
        [scoringSystems, score]
    );

    if (scoringSystemsLoading || !scoringSystems.length) {
        return null;
    }

    return (
        <Grid container spacing={2}>
            <Grid size={4}>
                <NumberField
                    control={control}
                    label={translate('certificateForm.scoring')}
                    name={`${name}.score`}
                />
            </Grid>

            <Grid size={8}>
                <SelectField
                    control={control}
                    label={translate('certificateForm.scoringSystems')}
                    name={`${name}.scoringSystemId`}
                    options={scoringSystemsOptions}
                />
            </Grid>
        </Grid>
    );
};
